import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useSnack } from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';

import { getSoknadScreenPath, RegistrationCommonStep, SoknadScreenParams } from '../../../utils/navigation';
import { getDetailingSteps } from '../../../store/registration/selectors';
import { getSteps } from '../../registration/useRegistrationForm/utils';
import { useUpdateRegistration } from '../../registration/useRegistrationForm/useUpdateRegistration';
import { createErrorMessage } from '../../../utils/utils';
import { useUser } from '../../../hooks/useUser';
import { DetailingStep, SoknadSteg } from '../../../components/registering/utils/registerEnums';
import { updateRegistration as updateRegistrationAction } from '../../../store/registration/actions';
import { useCreateRegistration } from '../../registration/useRegistrationForm/useCreateRegistration';
import { Energimerkeversjon } from '../../../types/energimerke';

export const useSoknadSubmit = () => {
  const { addSnack } = useSnack();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const store = useStore();
  const detailingSteps = useSelector(getDetailingSteps, _.isEqual);
  const { soknadSteg, buildingId, soknadId } = useParams<SoknadScreenParams>();
  const { updateRegistration, updateIsLoading } = useUpdateRegistration();
  const { createRegistration, createIsLoading } = useCreateRegistration(buildingId);
  const steps = getSteps(user, detailingSteps, soknadSteg);

  const onCreateSoknadRegistration = (energimerkeversjon: Energimerkeversjon) => {
    createRegistration(
      {
        soknadSteg: soknadSteg || SoknadSteg.Steg1,
        soknadId: soknadId,
        energimerkeversjon
      },
      {
        onSuccess: (data) => {
          if (!data.soknadId || !data.soknadSteg) {
            throw new Error('Invalid søknad');
          }

          // Update redux state with data from created registration
          dispatch(updateRegistrationAction({ userInput: data.userInput }));
          addSnack(t('registerScreen.saveRegistration.saveSucceeded'));

          // On new soknad registration navigate to building detailing step
          const soknadPath = getSoknadScreenPath({
            soknadSteg: data.soknadSteg,
            step: DetailingStep.Bygningsform,
            soknadId: data.soknadId,
            buildingId: data.energiObjektId
          });

          navigate(soknadPath);
        }
      }
    );
  };

  const onSubmit = (energimerkeversjon: Energimerkeversjon, registrationId?: string) => {
    const userInput = store.getState().register.userInput;
    const nextStep = steps[steps.indexOf(RegistrationCommonStep.ABOUT) + 1];

    // TODO: Rewrite to explicit handle creation or update of soknad registration
    if (!registrationId) {
      onCreateSoknadRegistration(energimerkeversjon);
      return;
    }

    updateRegistration(
      { registrationId, userInput },
      {
        onSuccess: () => navigate(`../../${nextStep}`),
        onError: () => {
          const errorMessage = createErrorMessage(t('registerScreen.saveRegistration.saveFailed'));

          addSnack(errorMessage, { variant: 'error' });
        }
      }
    );
  };

  return {
    onSoknadSubmit: onSubmit,
    onCreateSoknadRegistration,
    isLoading: updateIsLoading || createIsLoading
  };
};
